import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, EMPTY } from 'rxjs';
import {
	map,
	switchMap,
	catchError,
	withLatestFrom,
	filter,
	mergeMap
} from 'rxjs/operators';

import * as RolePermissionActions from '../actions/role-permission.actions';
import * as UserActions from '@app/store/actions/user.actions';
import { AuthService } from '@core/services/auth.service';
import {
	selectRolePermissionCurrentUsername,
	selectRolePermissionIsCacheValid,
	selectRolePermissionCachedForUsername
} from '../selectors/role-permission.selectors';
import { ROLE_PERMISSION_CACHE_CONFIG } from '../state/role-permission.state';

@Injectable()
export class RolePermissionEffects {
	private readonly actions$ = inject(Actions);
	private readonly store = inject(Store);
	private readonly authService = inject(AuthService);

	/**
	 * Load role and permission menu from API
	 */
	loadRoleAndPermissionMenu$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RolePermissionActions.loadRoleAndPermissionMenu),
			switchMap(({ username, forceRefresh }) => {
				return this.authService.getRoleAndPermissionMenu(username).pipe(
					map((response) => {
						// Store in localStorage for persistence
						this.storeCacheData(response, username);

						return RolePermissionActions.loadRoleAndPermissionMenuSuccess(
							{
								roleAndPermissionMenu: response,
								username
							}
						);
					}),
					catchError((error) => {
						return of(
							RolePermissionActions.loadRoleAndPermissionMenuFailure(
								{
									error,
									username
								}
							)
						);
					})
				);
			})
		)
	);

	/**
	 * Load role and permission menu from cache
	 */
	loadFromCache$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RolePermissionActions.loadFromCache),
			switchMap(({ username }) => {
				const cachedData = this.getCacheData(username);

				if (cachedData) {
					return of(
						RolePermissionActions.loadFromCacheSuccess({
							roleAndPermissionMenu: cachedData.data,
							username
						})
					);
				} else {
					return of(
						RolePermissionActions.loadFromCacheFailure({ username })
					);
				}
			}),
			catchError((error) => {
				return EMPTY;
			})
		)
	);

	/**
	 * Handle cache failure by triggering API load
	 */
	handleCacheFailure$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RolePermissionActions.loadFromCacheFailure),
			map(({ username }) => {
				return RolePermissionActions.loadRoleAndPermissionMenu({
					username,
					forceRefresh: false
				});
			})
		)
	);

	/**
	 * Handle user login - clear old cache and load fresh data
	 */
	handleUserLogin$ = createEffect(() =>
		this.actions$.pipe(
			ofType(UserActions.login),
			switchMap(({ user }) => {
				// Check if we have ApplicationUserName or EmployeeNumber as fallback
				const username =
					user?.ApplicationUserName ||
					user?.credentials?.EmployeeNumber;

				if (!username) {
					console.warn(
						'[RolePermission] No ApplicationUserName or EmployeeNumber available on login'
					);
					return EMPTY; // Return empty observable to do nothing
				}

				// Clear any existing cache (in case it's for a different user)
				try {
					const existingCache = localStorage.getItem(
						ROLE_PERMISSION_CACHE_CONFIG.CACHE_STORAGE_KEY
					);
					if (existingCache) {
						const parsed = JSON.parse(existingCache);
						if (parsed.username !== username) {
							localStorage.removeItem(
								ROLE_PERMISSION_CACHE_CONFIG.CACHE_STORAGE_KEY
							);
						}
					}
				} catch (error) {
					console.warn(
						'[RolePermission] Failed to check/clear existing cache:',
						error
					);
				}

				// Notify about user change first
				this.store.dispatch(
					RolePermissionActions.userChanged({ username })
				);

				// Then load fresh data (force refresh on login)
				return of(
					RolePermissionActions.loadRoleAndPermissionMenu({
						username,
						forceRefresh: true
					})
				);
			})
		)
	);

	/**
	 * Handle app hydration - check cache or load fresh data
	 */
	handleAppHydration$ = createEffect(() =>
		this.actions$.pipe(
			ofType(UserActions.hydrateSuccess),
			filter(({ state }) => {
				// Accept if we have ApplicationUserName or EmployeeNumber as fallback
				const hasUsername =
					!!state?.user?.ApplicationUserName ||
					!!state?.user?.credentials?.EmployeeNumber;
				return hasUsername;
			}),
			withLatestFrom(
				this.store.select(selectRolePermissionCurrentUsername),
				this.store.select(selectRolePermissionIsCacheValid),
				this.store.select(selectRolePermissionCachedForUsername)
			),
			mergeMap(
				([
					{ state },
					currentUsername,
					isCacheValid,
					cachedForUsername
				]) => {
					// Use ApplicationUserName if available, otherwise fallback to EmployeeNumber
					const username =
						state.user.ApplicationUserName ||
						state.user.credentials?.EmployeeNumber;

					// Notify about user (might be same or different)
					const userChangedAction = RolePermissionActions.userChanged(
						{ username }
					);

					// Check if we have valid cache for this user
					const hasValidCache =
						isCacheValid && cachedForUsername === username;

					if (hasValidCache) {
						return [
							userChangedAction,
							RolePermissionActions.loadFromCache({ username })
						];
					} else {
						return [
							userChangedAction,
							RolePermissionActions.loadRoleAndPermissionMenu({
								username,
								forceRefresh: false
							})
						];
					}
				}
			)
		)
	);

	/**
	 * Handle user logout - clear state and cache
	 */
	handleUserLogout$ = createEffect(() =>
		this.actions$.pipe(
			ofType(UserActions.logout),
			map(() => {
				// Clear localStorage cache
				try {
					localStorage.removeItem(
						ROLE_PERMISSION_CACHE_CONFIG.CACHE_STORAGE_KEY
					);
				} catch (error) {
					console.warn(
						'[RolePermission] Failed to clear cache on logout:',
						error
					);
				}

				return RolePermissionActions.resetState();
			})
		)
	);

	/**
	 * Store data in localStorage cache
	 */
	private storeCacheData(data: any, username: string): void {
		try {
			const cacheData = {
				data,
				username,
				timestamp: Date.now()
			};

			localStorage.setItem(
				ROLE_PERMISSION_CACHE_CONFIG.CACHE_STORAGE_KEY,
				JSON.stringify(cacheData)
			);
		} catch (error) {
			console.error('[RolePermission] Failed to cache data:', error);
		}
	}

	/**
	 * Get data from localStorage cache
	 */
	private getCacheData(
		username: string
	): { data: any; timestamp: number } | null {
		try {
			const cachedData = localStorage.getItem(
				ROLE_PERMISSION_CACHE_CONFIG.CACHE_STORAGE_KEY
			);
			if (!cachedData) {
				return null;
			}

			const parsed = JSON.parse(cachedData);

			// Verify cache is for the correct user
			if (parsed.username !== username) {
				return null;
			}

			// Check if cache is still valid (not expired)
			const now = Date.now();
			const cacheAge = now - parsed.timestamp;

			if (cacheAge > ROLE_PERMISSION_CACHE_CONFIG.CACHE_DURATION_MS) {
				return null;
			}

			return {
				data: parsed.data,
				timestamp: parsed.timestamp
			};
		} catch (error) {
			console.error('[RolePermission] Failed to read cache data:', error);
			return null;
		}
	}
}
